import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Loader } from "@/components/common/Loader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { CONTENT_SCHEMAS, STATUS_OPTIONS } from "@/config/contentSchemas";
import { AdminImageInput } from "@/components/admin/AdminImageInput";
import { AiSuggestButton } from "@/components/editor/AiField";
import { useSite } from "@/context/SiteContext";
import { ArrowLeft, Save } from "lucide-react";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (val) => (val ? new Date(val).toISOString() : null);

export default function AdminContentEditor() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const schema = CONTENT_SCHEMAS[type];
  const { settings } = useSite();
  const isNew = id === "new";
  const [form, setForm] = useState({ status: "draft", order: 0, start_at: null, end_at: null });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew && schema) {
      api.get(`/admin/${type}/${id}`).then((r) => setForm(r.data)).catch(() => toast.error("Could not load item")).finally(() => setLoading(false));
    }
    // eslint-disable-next-line
  }, [type, id]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const renderField = (field) => {
    const val = form[field.name];
    switch (field.type) {
      case "textarea":
        return <Textarea rows={4} value={val || ""} onChange={(e) => set(field.name, e.target.value)} data-testid={`field-${field.name}`} className="mt-1.5" />;
      case "list":
        return <><Textarea rows={5} value={Array.isArray(val) ? val.join("\n") : (val || "")} onChange={(e) => set(field.name, e.target.value)} data-testid={`field-${field.name}`} className="mt-1.5" placeholder="One item per line — press Enter for a new line" /><p className="mt-1 text-xs text-muted-foreground">One item per line. Each line becomes its own bullet.</p></>;
      case "checkbox":
        return <div className="mt-2 flex items-center gap-2"><Switch checked={!!val} onCheckedChange={(v) => set(field.name, v)} data-testid={`field-${field.name}`} /><span className="text-sm text-muted-foreground">{val ? "Yes" : "No"}</span></div>;
      case "number":
        return <Input type="number" value={val ?? ""} onChange={(e) => set(field.name, e.target.value === "" ? null : Number(e.target.value))} data-testid={`field-${field.name}`} className="mt-1.5" />;
      case "datetime":
        return <Input type="datetime-local" value={toLocalInput(val)} onChange={(e) => set(field.name, fromLocalInput(e.target.value))} data-testid={`field-${field.name}`} className="mt-1.5" />;
      case "image":
        return <AdminImageInput value={val} onChange={(v) => set(field.name, v)} testid={`field-${field.name}`} />;
      case "multiselect":
        {
          const fromSetting = field.optionsFromSetting ? settings?.[field.optionsFromSetting] : null;
          const dynamicOpts = Array.isArray(fromSetting) && fromSetting.length
            ? fromSetting.map((o) => (typeof o === "string" ? { value: o, label: o } : o))
            : null;
          const opts = dynamicOpts || field.options || [];
          const selected = Array.isArray(val) ? val : (val ? [val] : []);
          const toggle = (v) => set(field.name, selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
          const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          return (
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid={`field-${field.name}`}>
              {opts.length === 0 ? (
                <p className="text-xs text-muted-foreground">No filters yet — add them in &quot;Manage Filters&quot; below.</p>
              ) : opts.map((o) => {
                const on = selected.includes(o.value);
                return (
                  <button key={o.value} type="button" onClick={() => toggle(o.value)} aria-pressed={on} data-testid={`field-${field.name}-opt-${slug(o.value)}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors duration-200 ${on ? "border-brand bg-brand text-white" : "border-border bg-transparent text-muted-foreground hover:border-[hsl(var(--brand-red))]"}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          );
        }
      case "select":
        {
          const fromSetting = field.optionsFromSetting ? settings?.[field.optionsFromSetting] : null;
          const dynamicOpts = Array.isArray(fromSetting) && fromSetting.length
            ? fromSetting.map((o) => (typeof o === "string" ? { value: o, label: o } : o))
            : null;
          const opts = dynamicOpts || field.options || [];
          return (
            <Select value={val || ""} onValueChange={(v) => set(field.name, v)}>
              <SelectTrigger className="mt-1.5" data-testid={`field-${field.name}`}><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          );
        }
      default:
        return <Input value={val || ""} onChange={(e) => set(field.name, e.target.value)} data-testid={`field-${field.name}`} className="mt-1.5" />;
    }
  };

  const save = async (publish) => {
    const primaryVal = form[schema.primary];
    if (!primaryVal) { toast.error(`${schema.fields[0].label} is required`); return; }
    setSaving(true);
    const payload = { ...form };
    // Normalize "list" fields: allow free-form editing (raw string with line
    // breaks) but persist as a clean array (one item per non-empty line).
    (schema.fields || []).forEach((f) => {
      if (f.type === "list") {
        const v = payload[f.name];
        const arr = Array.isArray(v) ? v : String(v || "").split("\n");
        payload[f.name] = arr.map((s) => s.trim()).filter(Boolean);
      }
    });
    if (publish !== undefined) payload.status = publish ? "published" : "draft";
    // keep title in sync for list display + status calc
    payload.title = primaryVal;
    delete payload.computed_status;
    try {
      if (isNew) { await api.post(`/admin/${type}`, payload); toast.success("Created"); }
      else { await api.put(`/admin/${type}/${id}`, payload); toast.success("Saved"); }
      navigate(`/admin/${type}`);
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!schema) return <AdminLayout><p>Unknown type</p></AdminLayout>;
  if (loading) return <AdminLayout><Loader /></AdminLayout>;

  return (
    <AdminLayout>
      <button onClick={() => navigate(`/admin/${type}`)} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to {schema.label}</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="display-title text-4xl">{isNew ? `New ${schema.singular}` : `Edit ${schema.singular}`}</h1>
        {!isNew && form.computed_status && <StatusBadge status={form.computed_status} />}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {schema.fields.map((field) => (
            <div key={field.name}>
              <div className="flex items-center justify-between gap-2">
                <Label>{field.label}{field.required && " *"}</Label>
                {(field.type === "text" || field.type === "textarea") && (
                  <AiSuggestButton field={field.name} sectionType={type} pageName={schema.label} current={form[field.name]} onApply={(v) => set(field.name, v)} testid={`field-${field.name}-ai`} />
                )}
              </div>
              {renderField(field)}
              {field.help && <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>}
            </div>
          ))}
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-condensed text-sm font-semibold uppercase tracking-wide">Publishing</h3>
            <div className="mt-4 space-y-4">
              <div>
                <Label>Status</Label>
                <Select value={form.status || "draft"} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Display Order</Label><Input type="number" value={form.order ?? 0} onChange={(e) => set("order", Number(e.target.value))} data-testid="field-order" className="mt-1.5" /></div>
              <div><Label>Start Date (Scheduled)</Label><Input type="datetime-local" value={toLocalInput(form.start_at)} onChange={(e) => set("start_at", fromLocalInput(e.target.value))} data-testid="field-start_at" className="mt-1.5" /></div>
              <div><Label>End Date (Expires)</Label><Input type="datetime-local" value={toLocalInput(form.end_at)} onChange={(e) => set("end_at", fromLocalInput(e.target.value))} data-testid="field-end_at" className="mt-1.5" /></div>
              <p className="text-xs text-muted-foreground">Published items show on the site only between Start and End dates (if set).</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => save(true)} disabled={saving} data-testid="admin-publish-button" className="inline-flex items-center justify-center gap-2 rounded-md bg-brand px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-60"><Save className="h-4 w-4" /> Save & Publish</button>
            <button onClick={() => save(false)} disabled={saving} data-testid="admin-save-button" className="inline-flex items-center justify-center rounded-md border border-border px-5 py-3 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors disabled:opacity-60">Save as Draft</button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
